import { getSDK, hasSDK } from '@littlebird/sdk';
import { KBarSearch, useKBar } from 'kbar';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';

import { Icons } from '@/assets/icons/icons';
import { XStack } from '@/components/ui';
import { cn } from '@/lib/utils';
import { uiStore } from '@/stores/ui.store';

import { CommandBarFooter } from './cmd-bar-footer';
import { CommandBarPreview } from './cmd-bar-preview';
import { COMMAND_BAR_BODY_HEIGHT, CommandBarResults } from './cmd-bar-results';
import { shouldEnterSubmenuOnArrowRight } from './preview-model';
import { useMatches } from './use-matches';
import { isSearchQuery } from './use-search-cmd-bar-actions';

type Props = {
  totalCount: number;
  sectionTotals: Record<string, number>;
};

export const CommandBarBody = observer(function CommandBarBody({
  totalCount,
  sectionTotals,
}: Props) {
  useEffect(() => {
    uiStore.cancelCommandBarStateReset();
    const keptQuery = uiStore.commandBarQuery;
    if (isSearchQuery(keptQuery) && hasSDK()) {
      getSDK().searchStore.search(keptQuery);
    }
    return () => uiStore.scheduleCommandBarStateReset();
  }, []);
  const { results, rootActionId } = useMatches();
  const { query, searchQuery, activeIndex, currentRootActionId } = useKBar(
    (state) => ({
      searchQuery: state.searchQuery,
      activeIndex: state.activeIndex,
      currentRootActionId: state.currentRootActionId,
    })
  );

  const scope = uiStore.commandBarScope;
  const showScope =
    isSearchQuery(uiStore.commandBarQuery) && !currentRootActionId;
  const scopeCount = scope === 'All' ? totalCount : (sectionTotals[scope] ?? 0);

  return (
    <>
      <XStack className="h-[52px] items-center gap-2.5 border-b border-border-strong pl-4 pr-3">
        <Icons.search
          size={16}
          className="flex-shrink-0 text-foreground-muted"
        />
        <KBarSearch
          defaultValue={uiStore.commandBarQuery}
          onKeyDown={(e) => {
            if (e.key === 'Tab' && !currentRootActionId) {
              e.preventDefault();
              e.stopPropagation();
              uiStore.cycleCommandBarScope(e.shiftKey ? -1 : 1);
              return;
            }
            const item = results[activeIndex];
            if (
              typeof item !== 'string' &&
              shouldEnterSubmenuOnArrowRight(e, item)
            ) {
              e.preventDefault();
              query.setSearch('');
              query.setCurrentRootAction(item.id);
            }
          }}
          defaultPlaceholder="Type a command or search..."
          className={cn(
            'min-w-0 flex-1 text-base',
            'outline-none',
            'bg-transparent text-foreground',
            'disabled:text-foreground-subtle',
            'placeholder:text-foreground-muted'
          )}
        />
        {showScope && scopeCount > 0 && (
          <span className="flex-shrink-0 text-xs text-foreground-muted">
            {scopeCount} {scopeCount === 1 ? 'result' : 'results'}
          </span>
        )}
        {showScope && (
          <button
            type="button"
            tabIndex={-1}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => uiStore.cycleCommandBarScope(1)}
            className={cn(
              'flex h-[26px] flex-shrink-0 items-center gap-1.5 rounded-md px-2 text-xs',
              'border border-border-strong bg-surface-hover text-foreground-muted'
            )}
          >
            {scope}
            <kbd className="text-[11px] text-foreground-subtle">tab</kbd>
          </button>
        )}
      </XStack>
      <div
        className={cn(
          'grid grid-cols-[300px_minmax(0,1fr)] max-[760px]:grid-cols-1',
          '[&_*]:outline-none [&_*]:ring-0',
          '[&_*:focus-visible]:outline-none [&_*:focus-visible]:ring-0'
        )}
        style={{ height: COMMAND_BAR_BODY_HEIGHT }}
      >
        <div className="min-w-0 overflow-hidden border-r border-border-strong max-[760px]:border-r-0">
          <CommandBarResults
            results={results}
            rootActionId={rootActionId}
            query={searchQuery}
            sectionTotals={sectionTotals}
          />
        </div>
        <div className="min-w-0 max-[760px]:hidden">
          <CommandBarPreview results={results} />
        </div>
      </div>
      <CommandBarFooter results={results} showScope={showScope} />
    </>
  );
});
